import React, { memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Drawer } from 'antd';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { parseDateOnly } from 'services/DateService';
import { Colors } from 'context';
import { Button, ButtonSizes, Icon, IconNames } from 'atoms';
import { formatSalaryRange, ICareerVacancy } from 'core';

export interface ICareerVacancyDetails {
  /** `null` держит панель закрытой — открытость выводится из данных. */
  vacancy: ICareerVacancy | null;
  onClose: () => void;
  onSave: () => void;
  actionLoading: boolean;
}

/**
 * Подробный вид вакансии.
 *
 * Зачем понадобился: в карточке описание обрезано тремя строками
 * (`-webkit-line-clamp`), а прочитать его целиком было НЕГДЕ — отдельной
 * страницы вакансии в LMS нет. Владелец на ручной проверке так и сказал:
 * непонятно, что это за вакансия и что с ней делать.
 *
 * Данные берутся из уже загруженного списка, отдельного запроса нет: лента
 * и так тянет все поля, а лишний round-trip на клик по карточке ничем не
 * оправдан.
 */
export const CareerVacancyDetails = memo(
  ({ vacancy, onClose, onSave, actionLoading }: ICareerVacancyDetails) => {
    const { t } = useTranslation();

    if (!vacancy) {
      return null;
    }

    const isSaved = vacancy.interaction === 'saved' || vacancy.interaction === 'applied';
    const salary = formatSalaryRange(vacancy.salaryMin, vacancy.salaryMax, vacancy.salaryCurrency);

    const facts: { key: string; icon: IconNames; label: string; value: string }[] = [
      {
        key: 'location',
        icon: IconNames.VacancyLocation,
        label: t('careerCenter.vacancies.facts.location'),
        value: vacancy.location,
      },
      {
        key: 'workMode',
        icon: IconNames.VacancyMode,
        label: t('careerCenter.vacancies.facts.workMode'),
        value: vacancy.workMode
          ? t(`careerProfile.workMode.${vacancy.workMode}`, { defaultValue: vacancy.workMode })
          : '',
      },
      {
        key: 'seniority',
        icon: IconNames.VacancyLevel,
        label: t('careerCenter.vacancies.facts.seniority'),
        value: vacancy.seniority
          ? t(`careerProfile.seniority.${vacancy.seniority}`, { defaultValue: vacancy.seniority })
          : '',
      },
      {
        key: 'employment',
        icon: IconNames.VacancyEmployment,
        label: t('careerCenter.vacancies.facts.employment'),
        value: vacancy.employmentType
          ? t(`careerCenter.vacancies.employment.${vacancy.employmentType}`, {
              defaultValue: vacancy.employmentType,
            })
          : '',
      },
      {
        key: 'salary',
        icon: IconNames.VacancySalary,
        label: t('careerCenter.vacancies.facts.salary'),
        value: salary,
      },
      {
        key: 'expires',
        icon: IconNames.Calendar,
        label: t('careerCenter.vacancies.expiresAt'),
        value: vacancy.expiresAt ? parseDateOnly(vacancy.expiresAt) : '',
      },
    ].filter((fact) => Boolean(fact.value));

    return (
      <Drawer open onClose={onClose} width={560} title={vacancy.title} destroyOnClose>
        <CompanyLine>
          {vacancy.companyName}
          {vacancy.location ? ` · ${vacancy.location}` : ''}
        </CompanyLine>

        {facts.length ? (
          <FactList>
            {facts.map((fact) => (
              <FactRow key={fact.key}>
                <FactIcon>
                  <Icon name={fact.icon} width="16px" height="16px" />
                </FactIcon>
                <FactLabel>{fact.label}</FactLabel>
                <FactValue>{fact.value}</FactValue>
              </FactRow>
            ))}
          </FactList>
        ) : null}

        {vacancy.description ? (
          <Section>
            <SectionTitle>{t('careerCenter.vacancies.facts.description')}</SectionTitle>
            {/* Описание — свободный текст из ERP; переносы строк сохраняем. */}
            <DescriptionText>{vacancy.description}</DescriptionText>
          </Section>
        ) : null}

        {vacancy.directions.length ? (
          <Section>
            <SectionTitle>{t('careerCenter.vacancies.facts.directions')}</SectionTitle>
            <TagRow>
              {vacancy.directions.map((direction) => (
                <Tag key={direction.id}>{direction.title}</Tag>
              ))}
            </TagRow>
          </Section>
        ) : null}

        {/* В карточке навыки обрезаны восемью — здесь показываем все. */}
        {vacancy.skillNames.length ? (
          <Section>
            <SectionTitle>{t('careerCenter.vacancies.facts.skills')}</SectionTitle>
            <TagRow>
              {vacancy.skillNames.map((skill) => (
                <Tag key={skill}>{skill}</Tag>
              ))}
            </TagRow>
          </Section>
        ) : null}

        <Footer>
          <SaveButton
            variant="outline"
            size={ButtonSizes.sm}
            disabled={actionLoading}
            onClick={onSave}>
            {t(isSaved ? 'careerCenter.vacancies.unsave' : 'careerCenter.vacancies.save')}
          </SaveButton>

          <OpenLink href={vacancy.applyUrl} target="_blank" rel="noopener noreferrer">
            {t('careerCenter.vacancies.open')}
          </OpenLink>
        </Footer>

        <FooterNote>{t('careerCenter.vacancies.applyElsewhere')}</FooterNote>
      </Drawer>
    );
  },
);

const CompanyLine = styled.div`
  margin-bottom: 20px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const FactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: 12px;
  background-color: ${Colors.bg_gray};
`;

const FactRow = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr auto;
  align-items: center;
  gap: 10px;
`;

const FactIcon = styled.span`
  display: inline-flex;
  color: ${Colors.gray_dark};
`;

const FactLabel = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const FactValue = styled.span`
  text-align: right;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Section = styled.div`
  margin-top: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 8px 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const DescriptionText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  padding: 4px 10px;
  border-radius: 8px;
  background-color: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
`;

const SaveButton = styled(Button)`
  width: auto;
`;

const OpenLink = styled.a`
  padding: 10px 18px;
  border-radius: 10px;
  background-color: ${Colors.main};
  color: ${Colors.white};
  white-space: nowrap;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    opacity: 0.9;
    color: ${Colors.white};
  }
`;

/** Честная подпись: откликнуться из системы нельзя, отклик идёт у работодателя. */
const FooterNote = styled.p`
  margin: 12px 0 0 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.regular })}
`;
